import { isNull } from "lodash";

import { API } from "aws-amplify";

import { getAPIReq } from "data/libs/authLib";

import { mapUUID, toGlobalId } from "./helper";

export const toClientCardTask = (task, cwNode) => {
  return {
    ...task,
    id: task.uuid,
    clientWorkflowId: task.clientWorkflow
      ? task.clientWorkflow.uuid
      : undefined,
    householdMembers: task.householdMembers.edges.map(({ node: h }) => {
      return {
        ...h,
        id: h.uuid,
        householdId: h.household.uuid,
        clientId: h.client.uuid,
        client: {
          ...h.client,
          id: h.client.uuid,
          name: h.client.name
            ? h.client.name
            : `${h.client.firstName} ${h.client.lastName}`,
        },
      };
    }),
    emailTemplate: task.emailTemplate
      ? { ...task.emailTemplate, id: task.emailTemplate.uuid }
      : null,
    timeEstimates: task.timeEstimates.edges.map(({ node: timeNode }) => {
      return {
        ...timeNode,
        id: timeNode.uuid,
        addedBy: timeNode.addedBy
          ? {
              ...timeNode.addedBy,
              id: timeNode.addedBy.uuid,
              name: `${timeNode.addedBy.firstName} ${timeNode.addedBy.lastName}`,
            }
          : undefined,
      };
    }),
    assignee: mapUUID(task.assignee),
    assignedAdvisor: task.assignedAdvisor
      ? { ...task.assignedAdvisor, id: task.assignedAdvisor.uuid }
      : null,
    assignedAdvisorId: task.assignedAdvisor
      ? task.assignedAdvisor.uuid
      : undefined,
    reminders: task.reminders.edges.map(({ node: rNode }) => {
      return {
        ...rNode,
        id: rNode.uuid,
        taskId: rNode.task.uuid,
        clientWorkflowId: cwNode.uuid,
      };
    }),
    links: task.links.edges.map(({ node: lNode }) => {
      return {
        ...lNode,
        id: lNode.uuid,
      };
    }),
    comments: task.comments?.edges
      ? task.comments.edges.map(({ node: cNode }) => {
          return {
            ...cNode,
            id: cNode.uuid,
          };
        })
      : [],
    workflowName: cwNode.workflow.name,
    formInstanceId: task.formInstance?.uuid,
  };
};

export const toClientCardNextWorkflow = (n, cwNode) => {
  return {
    ...n,
    id: n.uuid,
    nextWorkflowName: n.nextWorkflow.name,
    nextWorkflowId: n.nextWorkflow.uuid,
    nextWorkflowMultipleTimePerClient: n.nextWorkflow.multipleTimePerClient,
    clientWorkflowId: n.clientWorkflow.uuid,
    task: n.task
      ? {
          ...n.task,
          id: n.task.uuid,
          assignedAdvisor: n.task.assignedAdvisor
            ? {
                ...n.task.assignedAdvisor,
                id: n.task.assignedAdvisor.uuid,
              }
            : undefined,
          reminders: n.task.reminders.edges.map(({ node: r }) => {
            return {
              ...r,
              id: r.uuid,
              taskId: r.task.uuid,
              clientWorkflowId: cwNode.uuid,
            };
          }),
        }
      : undefined,
  };
};

const toConditionEvaluable = ({
  value,
  streams,
  tags,
  nestedConditionGroup,
  ...condition
}) => {
  const v = isNull(Object.values(value)[0])
    ? undefined
    : Object.values(value)[0];

  return {
    ...condition,
    nestedConditionGroup: nestedConditionGroup
      ? nestedConditionGroup.conditions.edges.map(toConditionEvaluable)
      : null,
    value: v,
    formField: condition.formFieldValue,
    streams: streams.edges.map(({ node }) => {
      return node;
    }),
    tags: tags.edges.map(({ node }) => {
      return node;
    }),
  };
};

export const toConditionsGroupEvaluable = (
  { task, nextworkflow, conditions, ...conditionsGroup },
  cwNode
) => {
  const conditionsGroupObj = {
    ...conditionsGroup,
    conditions: conditions.edges.map(({ node: cNode }) => {
      return toConditionEvaluable(cNode);
    }),
  };

  if (task) {
    conditionsGroupObj.task = task.edges.map(({ node: tNode }) => {
      return toClientCardTask(tNode, cwNode);
    });
  }

  if (nextworkflow) {
    conditionsGroupObj.nextWorkflow = nextworkflow.edges.map(
      ({ node: nNode }) => {
        return toClientCardNextWorkflow(nNode, cwNode);
      }
    );
  }

  return conditionsGroupObj;
};

export const getAllClientames = async ({
  hubId,
  workflowId,
  workflowIds,
  filterInput,
  searchText = "",
  first = 50,
  after = "",
}) => {
  const req = await getAPIReq(
    {
      query: `query getAllClientsNames(
          $searchText: String,
          $filterInput: FilterInput,
          $workflowIds: [ID],
          $workflowId: ID,
          $first: Int,
          $after: String
        ) {
          searchClients(
            searchText: $searchText,
            filterInput: $filterInput,
            workflowIds: $workflowIds,
            excludeWorkflowId: $workflowId,
            first: $first,
            after: $after
          ) {
            edges {
              node {
                id
                uuid
                name
                household {
                  id
                  uuid
                }
                isDeletedInCrm
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
          `,
      variables: {
        searchText: searchText,
        filterInput: filterInput
          ? {
              streams: filterInput.streams.map(({ id }) => {
                return toGlobalId("Stream", id);
              }),
              streamFilterType: filterInput.streamFilterType,
              tags: filterInput.tags.map(({ id }) => {
                return toGlobalId("Tag", id);
              }),
              tagFilterType: filterInput.tagFilterType,
              filterNextTaskForAssignee: filterInput.filterNextTaskForAssignee,
              assignee: filterInput.assignee
                ? toGlobalId("Advisor", filterInput.assignee)
                : null,
              assigneeFilterType: filterInput.assigneeFilterType,
              filterAssigneeIncludeServiceRole:
                filterInput.filterAssigneeIncludeServiceRole,
              filterServiceRoleUnassignedOnly:
                filterInput.filterServiceRoleUnassignedOnly,
              reminderTypes: filterInput.reminderTypes,
              clientFilter: [],
              filterEmptyWorkflows: false,
              showAll: false,
            }
          : null,
        workflowIds: workflowIds?.map((id) => {
          return toGlobalId("Workflow", id);
        }),
        workflowId: workflowId ? toGlobalId("Workflow", workflowId) : null,
        first: first,
        after: after,
      },
    },
    { hub: hubId }
  );

  const {
    data: { searchClients },
  } = await API.post("HublyAPI", `/graphql`, req);
  return {
    data: searchClients.edges.map(({ node }) => {
      return {
        ...node,
        id: node.uuid,
        household: node.household ? { id: node.household.uuid } : {},
      };
    }),
    pageInfo: searchClients.pageInfo,
  };
};

export const getRecentClients = async ({ hubId }) => {
  const req = await getAPIReq(
    {
      query: `query getRecentClients {
            recentClients {
                teamRecentlyOpened {
                    id
                    uuid
                    name
                    household {
                      id
                      uuid
                    }
                    isDeletedInCrm
                }
                recentlyOpened {
                    id
                    name
                    uuid
                    household {
                      id
                      uuid
                    }
                    isDeletedInCrm
                }
            }
        }
      `,
    },
    { hub: hubId }
  );

  const {
    data: { recentClients },
  } = await API.post("HublyAPI", `/graphql`, req);
  return {
    recentNames: recentClients.recentlyOpened.map((c) => {
      return {
        ...c,
        id: c.uuid,
        household: c.household ? { id: c.household.uuid } : {},
      };
    }),
    teamRecentNames: recentClients.teamRecentlyOpened.map((c) => {
      return {
        ...c,
        id: c.uuid,
        household: c.household ? { id: c.household.uuid } : {},
      };
    }),
  };
};

export const getClientWorkflowsByClientId = async (
  { hubId, clientId },
  queryNumComments = false
) => {
  const commentsSubquery = queryNumComments
    ? ""
    : "comments { edges { node { uuid } } } taskComments { uuid }";
  const req = await getAPIReq(
    {
      query: `fragment TaskFields on Task {
        uuid
        title
        clientWorkflow {
          uuid
        }
        advisorDefinedCompletedAt
        description
        options
        completed
        completedAt
        isClientTask
        assignee {
          uuid
          name
          group
        }
        assignedAdvisor {
          uuid
          firstName
          lastName
        }
        order
        householdMembers {
          edges {
            node {
              uuid
              title
              type
              client {
                uuid
                firstName
                lastName
                name
              }
              household {
                uuid
              }
            }
          }
        }
        timeEstimates {
          edges {
            node {
              uuid
              autoAddedInd
              timeEstimate
              createdAt
              addedBy {
                uuid
                firstName
                lastName
              }
            }
          }
        }
        emailTemplate {
          uuid
          to
          subject
          body
        }
        reminders {
          edges {
            node {
              uuid
              time
              futureReminderNumber
              futureReminderType
              futureReminderTime
              task {
                uuid
              }
              dismissed
              completedAt
            }
          }
        }
        links {
          edges {
            node {
              uuid
              title
              url
            }
          }
        }
        ${commentsSubquery}
        formInstance {
          uuid
          isFilled
          name
        }
      }

      fragment NextWorkflowFields on NextWorkflow {
        uuid
        nextWorkflow {
          uuid
          name
          multipleTimePerClient
        }
        clientWorkflow {
          uuid
        }
        createdAt
        startDate
        task {
          uuid
          title
          assignedAdvisor {
            uuid
            firstName
            lastName
          }
          reminders {
            edges {
              node {
                uuid
                time
                futureReminderNumber
                futureReminderType
                futureReminderTime
                task {
                  uuid
                }
              }
            }
          }
        }
      }

      fragment ConditionEvaluableFields on ConditionEvaluable {
        id
        operator
        operand
        type
        name
        datePart
        workflowPart
        formFieldValue {
          name
          type {
            id
          }
        }
        value {
          ... on StringValue {
            stringValue
          }
          ... on ArrayStringValue {
            arrayStringValue
          }
        }
        tags {
          edges {
            node {
              id
              name
            }
          }
        }
        streams {
          edges {
            node {
              id
              name
              color
            }
          }
        }
      }

      query getClient($id: ID!) {
        client(id: $id) {
          workflows {
            edges {
              node {
                uuid
                name
                hideCompletedTasks
                completed
                completedAt
                createdAt
                workflow {
                  uuid
                  name
                }
                nextWorkflowsGroup {
                  ... on ConditionsGroupEvaluable {
                    id
                    nextworkflow {
                      edges {
                        node {
                          ...NextWorkflowFields
                          conditionStatus
                        }
                      }
                    }
                    conditions(first: 10) {
                      edges {
                        node {
                          ...ConditionEvaluableFields
                        }
                      }
                    }
                  }
                  ... on NextWorkflow {
                    ...NextWorkflowFields
                  }
                }
                tasksGroup {
                  ... on ConditionsGroupEvaluable {
                    id
                    task {
                      edges {
                        node {
                          ...TaskFields
                        }
                      }
                    }
                    conditions(first: 10) {
                      edges {
                        node {
                          ...ConditionEvaluableFields
                        }
                      }
                    }
                  }
                  ... on Task {
                    ...TaskFields
                  }
                }
                isHidden
                isNew
                archived
                assignedAdvisor {
                  uuid
                  firstName
                  lastName
                }
                previousWorkflow
                startedBy {
                  operatingEntityId
                  operationType
                  startedByText
                }
              }
            }
          }
        }
      }
      `,
      variables: {
        id: toGlobalId("Client", clientId),
      },
    },
    { hub: hubId }
  );

  const {
    data: { client },
  } = await API.post("HublyAPI", `/graphql`, req);

  return client.workflows.edges.map(({ node }) => {
    return {
      ...node,
      id: node.uuid,
      workflowId: node.workflow.uuid,
      clientId: clientId,
      nextWorkflows: node.nextWorkflowsGroup
        .filter((group) => {
          return !group?.conditions;
        })
        .map((nextWorkflow) => {
          return toClientCardNextWorkflow(nextWorkflow, node);
        }),
      nextWorkflowsGroup: node.nextWorkflowsGroup.map((group) => {
        return group?.conditions
          ? toConditionsGroupEvaluable(group, node)
          : toClientCardNextWorkflow(group, node);
      }),
      tasks: node.tasksGroup
        .filter((group) => {
          return !group?.conditions;
        })
        .map((t) => {
          return toClientCardTask(t, node);
        }),
      tasksGroup: node.tasksGroup.map((group) => {
        return group?.conditions
          ? toConditionsGroupEvaluable(group, node)
          : toClientCardTask(group, node);
      }),
      assignedAdvisorId: node.assignedAdvisor
        ? node.assignedAdvisor.uuid
        : undefined,
      assignedAdvisor: node.assignedAdvisor
        ? { ...node.assignedAdvisor, id: node.assignedAdvisor.uuid }
        : undefined,
      startedBy: node.startedBy
        ? {
            operationType: node.startedBy.operationType,
            operatingEntityId: node.startedBy.operatingEntityId,
          }
        : undefined,
      startedByText: node.startedBy?.startedByText,
    };
  });
};

export default getRecentClients;
